/*
  The table primitives' shared class strings (§6.2).

  TABLE_CELL_PADDING is the one §6.2 asks for by name: "one density across every table,
  expressed as a shared Tailwind row/cell padding class applied by the shared table
  primitive — not as prose. Checkable by grep." Every <th>, every <td> and every stacked
  card field uses it, so grepping TABLE_CELL_PADDING finds the density in one place.
*/

import { FOCUS_RING_INSET } from "components/styles";
import { ColumnAlign } from "./types";

/* THE density. Nothing in a table sets its own padding. */
export const TABLE_CELL_PADDING = "px-3 py-2.5";

/*
  The one exception, and it is a column property rather than a consumer's class: a
  `compact` column halves the side padding so sixteen grade columns fit across the set
  page (batch 4). Important, so it beats TABLE_CELL_PADDING whatever order Tailwind emits
  the two in.
*/
export const TABLE_CELL_PADDING_TIGHT = "!px-1.5";

/*
  border-separate, not Preflight's border-collapse.

  A sticky <th> loses its bottom border under border-collapse — the border belongs to the
  collapsed edge and scrolls away with the body — so the header would come unstuck-looking
  the moment the body moved. border-spacing-0 keeps the cells flush.
*/
export const TABLE_BASE =
  "skin-table w-full border-separate border-spacing-0 text-left text-sm text-pcg-text";

/*
  The scroll container (§6.3): a table wider than its column never scrolls the page body,
  it scrolls in here. The header sticks to the top of THIS box, never the viewport — which
  is only visible while the box is height-constrained, hence the default max-height.
*/
export const TABLE_SCROLL_AREA =
  `skin-surface relative overflow-auto overscroll-x-contain rounded-lg border border-pcg-border bg-pcg-card ${FOCUS_RING_INSET}`;

export const TABLE_SCROLL_AREA_MAX_HEIGHT = "max-h-[70vh]";

/*
  Column rules.

  The tables had horizontal borders only. That is fine at four columns and unusable at
  sixteen: a count sitting under "9.5" is separated from the count under "9" by nothing but
  whitespace, so reading a figure back up to the grade that owns it means tracking a column
  by eye across the full width of a scrolling matrix. A hairline between columns is what
  makes that traceable.

  border-l on every cell but the first, rather than border-r on every cell but the last:
  `first:` is a position the cell can know about, `last:` in a row whose cell count changes
  with "Show All Grades" is not worth relying on. borderSoft rather than border, because
  these rules are a reading aid and should sit under the row and header rules, not compete
  with them.
*/
const TABLE_COLUMN_RULE = "border-l border-pcg-borderSoft first:border-l-0";

/* Sticky is per-cell, not on <thead>: Safari only picked up a sticky thead in 14. */
export const TABLE_HEAD_CELL =
  `${TABLE_CELL_PADDING} ${TABLE_COLUMN_RULE} sticky top-0 z-10 border-b border-pcg-border bg-pcg-tableHeader text-xs font-semibold uppercase tracking-wide text-pcg-textMuted`;

export const TABLE_BODY_CELL =
  `${TABLE_CELL_PADDING} ${TABLE_COLUMN_RULE} border-b border-pcg-border align-top`;

/*
  Zebra + hover + focus. Tailwind emits hover: after even:, so the hovered row wins over
  its stripe without an !important. Zebra is deliberately absent from the stacked cards
  (§6.3) — a card already has its own border and ground.
*/
export const TABLE_ROW_ZEBRA = "even:bg-pcg-surfaceAlt";
export const TABLE_ROW_HOVER = "hover:bg-pcg-raised";
export const TABLE_ROW_FOCUS =
  "focus-within:ring-2 focus-within:ring-inset focus-within:ring-pcg-goldText";

export const STACKED_CARD =
  `skin-surface rounded-lg border border-pcg-border bg-pcg-card ${TABLE_ROW_HOVER} ${TABLE_ROW_FOCUS}`;

/* Alignment is resolved from the descriptor, never written by a consumer. */
export const ALIGN_CLASS: Record<ColumnAlign, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};
